import jsPDF from "jspdf";
import autoTable, { CellInput, RowInput, Styles, UserOptions } from "jspdf-autotable";
import crxLogo from "../assets/images/crx_header.png";
import plexRegular from "../assets/fonts/IBMPlexSans-Regular.ttf";
import plexSemiBold from "../assets/fonts/IBMPlexSans-SemiBold.ttf";
import plexItalic from "../assets/fonts/IBMPlexSans-Italic.ttf";
import { getReq } from "./request";

/*
 * CRX document style — the one look every PDF export shares.
 *
 * It mirrors the web's light theme (theme.scss `body` tokens): IBM Plex Sans,
 * the strong / heading / text / muted text roles, hairline surfaces, tonal
 * status pills, and the slate + gold brand on the page-one masthead. Builders
 * compose the helpers below and take colours from `pdfColors` / `pdfTones`;
 * they never pick their own.
 *
 * Page anatomy: page one opens with the masthead (slate band, then the title
 * block) or a cover page; every later page gets a running header; every page
 * gets the footer (rule, wordmark, centre text, page X of Y).
 */

export type PdfColor = [number, number, number];

const rgb = (hex: string): PdfColor => [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
];

/**
 * The light-theme tokens the PDF palette is built from, by their theme.scss
 * name. theme.test.ts-style guard: exportToPdfHelper.test.ts fails when a
 * value here drifts from theme.scss.
 */
export const PDF_THEME_TOKENS = {
    "--text-strong": "#1E2A2B",
    "--text-heading": "#33494B",
    "--text": "#3B4A4B",
    "--text-muted": "#5C7072",
    "--accent-ink": "#6B6F00",
    "--surface-border": "#D6E0E1",
    "--surface-card-header": "#F4F7F7",
    "--btn-secondary-border": "#C3D0D2",
    "--btn-primary-bg": "#3F4E4F",
    "--btn-primary-fg": "#D9E021",
    "--inverse-muted": "#A9BDBF",
    "--status-success-bg": "#E3F2E6",
    "--status-success-fg": "#1E5B2C",
    "--status-success-solid": "#2E7D3E",
    "--status-warning-bg": "#FFF1D6",
    "--status-warning-fg": "#7A4D00",
    "--status-warning-solid": "#9E5C00",
    "--status-danger-bg": "#FBE5E6",
    "--status-danger-fg": "#9E1F24",
    "--status-danger-solid": "#C1272D",
    "--status-info-bg": "#E3EEF7",
    "--status-info-fg": "#1F4E79",
    "--status-info-solid": "#2C6AA5",
    "--status-high-bg": "#FFE6D5",
    "--status-high-fg": "#8A3A00",
    "--status-high-solid": "#C4511A",
    "--status-neutral-bg": "#E9EEEF",
    "--status-neutral-fg": "#3F4E4F",
    "--status-neutral-solid": "#5F7476",
    "--status-purple-bg": "#F1E8F8",
    "--status-purple-fg": "#5B2A82",
    "--status-purple-solid": "#7B3FA8",
} as const;

const token = (name: keyof typeof PDF_THEME_TOKENS): PdfColor => rgb(PDF_THEME_TOKENS[name]);

/** Status tones — same names and colours as the web's tonal pills (ChipTone). */
export type PdfTone = "neutral" | "info" | "success" | "warning" | "danger" | "high" | "purple";

const toneOf = (name: PdfTone): { bg: PdfColor; fg: PdfColor; solid: PdfColor } => ({
    bg: token(`--status-${name}-bg` as keyof typeof PDF_THEME_TOKENS),
    fg: token(`--status-${name}-fg` as keyof typeof PDF_THEME_TOKENS),
    solid: token(`--status-${name}-solid` as keyof typeof PDF_THEME_TOKENS),
});

export const pdfTones: Record<PdfTone, { bg: PdfColor; fg: PdfColor; solid: PdfColor }> = {
    neutral: toneOf("neutral"),
    info: toneOf("info"),
    success: toneOf("success"),
    warning: toneOf("warning"),
    danger: toneOf("danger"),
    high: toneOf("high"),
    purple: toneOf("purple"),
};

const palette = {
    strong: token("--text-strong"),
    heading: token("--text-heading"),
    body: token("--text"),
    muted: token("--text-muted"),
    accentInk: token("--accent-ink"),
    border: token("--surface-border"),
    borderStrong: token("--btn-secondary-border"),
    surfaceHeader: token("--surface-card-header"),
    zebra: [248, 250, 250] as PdfColor,
    white: [255, 255, 255] as PdfColor,
    slate: token("--btn-primary-bg"),
    gold: token("--btn-primary-fg"),
    inverseMuted: token("--inverse-muted"),
};

/**
 * Document colours. The first block is the current vocabulary; the legacy
 * names below it are kept so older builders keep compiling, and now resolve
 * to the theme roles they always meant.
 */
export const pdfColors = {
    ...palette,
    /** Brand slate — masthead band, grand-total rows. */
    primary: palette.slate,
    secondary: palette.slate,
    /** Brand gold — only ever on slate. Gold-family text on white is `accentInk`. */
    accent: palette.gold,
    accentDark: palette.accentInk,
    /** Rules and frames. */
    button: palette.borderStrong,
    success: pdfTones.success.solid,
    warning: pdfTones.warning.solid,
    danger: pdfTones.danger.solid,
    light: palette.surfaceHeader,
    /** Secondary text: labels, captions, meta lines. */
    text: palette.muted,
    /** Headings and emphasised values. */
    headerText: palette.heading,
    dropList: pdfTones.neutral.bg,
};

// ============================================================================
// Layout + formatting
// ============================================================================

export const PDF_MARGIN_X = 14;
/** First content y on a continuation page — below the running header. */
export const PDF_CONTENT_TOP = 20;
/** Content stays this far above the page bottom — the footer lives there. */
export const PDF_FOOTER_RESERVE = 20;

const MASTHEAD_BAND_H = 13;
/** crx_header.png is 82 × 88 px. */
const LOGO_RATIO = 82 / 88;
const PANEL_RADIUS = 1.8;
const HAIRLINE = 0.25;
/** Tracking for small uppercase labels (≈ 0.06em, as on screen). */
const LABEL_TRACKING = 0.15;

/** "5 Oct 2026" — the web's long date. */
export const pdfDate = (value: string | Date | null | undefined): string =>
    value ? new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";

/** "5 Oct 2026, 14:30". */
export const pdfDateTime = (value: string | Date | null | undefined): string =>
    value ? new Date(value).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

/**
 * Characters both Plex and the Helvetica fallback can draw: Latin-1 plus the
 * typographic punctuation WinAnsi carries (dashes, quotes, bullet, ellipsis).
 */
const PDF_UNDRAWABLE = /[^\n\r\t\x20-\x7E\u00A0-\u00FF\u2013\u2014\u2018\u2019\u201A\u201C-\u201E\u2020-\u2022\u2026\u2030\u2039\u203A]/g;

/** Replaces characters the PDF fonts can't draw (non-Latin scripts, emoji, symbols). */
export const pdfDrawable = (text: string, replacement = ""): string => text.replace(PDF_UNDRAWABLE, replacement);

/** "12,400.50" — grouped, two decimals. */
export const pdfAmount = (value: number | string | null | undefined): string =>
    Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const pageWidthOf = (doc: jsPDF): number => doc.internal.pageSize.getWidth();
const pageHeightOf = (doc: jsPDF): number => doc.internal.pageSize.getHeight();
const currentPage = (doc: jsPDF): number => (doc as any).getCurrentPageInfo().pageNumber;

/** Adds a page when `neededMm` won't fit above the footer; returns the y to draw at. */
export const ensurePdfSpace = (doc: jsPDF, yPos: number, neededMm: number): number => {
    if (yPos + neededMm > pageHeightOf(doc) - PDF_FOOTER_RESERVE) {
        doc.addPage();
        return PDF_CONTENT_TOP;
    }
    return yPos;
};

/** Width of `text` drawn with `charSpace` tracking (jsPDF's measure ignores it). */
const trackedWidth = (doc: jsPDF, text: string, charSpace: number): number =>
    doc.getTextWidth(text) + charSpace * Math.max(0, text.length - 1);

/** First line of `text` that fits `maxWidth`, with an ellipsis when cut. */
const fitLine = (doc: jsPDF, text: string, maxWidth: number): string => {
    if (doc.getTextWidth(text) <= maxWidth) return text;
    let cut = text;
    while (cut.length > 1 && doc.getTextWidth(`${cut}…`) > maxWidth) cut = cut.slice(0, -1);
    return `${cut.trimEnd()}…`;
};

const setText = (doc: jsPDF, color: PdfColor, size: number, style: "normal" | "bold" | "italic" = "normal"): void => {
    doc.setTextColor(...color);
    doc.setFontSize(size);
    doc.setFont("helvetica", style);
};

const hairline = (doc: jsPDF, x1: number, y: number, x2: number, color: PdfColor = palette.border, width = HAIRLINE): void => {
    doc.setDrawColor(...color);
    doc.setLineWidth(width);
    doc.line(x1, y, x2, y);
};

// ============================================================================
// Fonts — IBM Plex Sans, the web's typeface
// ============================================================================

/*
 * Plex is registered under jsPDF's "helvetica" family name, so every existing
 * setFont("helvetica", …) call and autoTable's default font pick it up.
 * "bold" maps to SemiBold, the weight the web uses for titles and values.
 * The TTFs are fetched on the first export only (CRA serves them as hashed,
 * cacheable assets); if that fails the PDFs fall back to built-in Helvetica.
 */
const PDF_FONTS = [
    { file: "IBMPlexSans-Regular.ttf", url: plexRegular, styles: ["normal"] },
    { file: "IBMPlexSans-SemiBold.ttf", url: plexSemiBold, styles: ["bold", "bolditalic"] },
    { file: "IBMPlexSans-Italic.ttf", url: plexItalic, styles: ["italic"] },
];

let pdfFontData: Map<string, string> | null = null;
let pdfFontLoad: Promise<void> | null = null;

const toBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.length; i += 0x8000) {
        binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)));
    }
    return btoa(binary);
};

/**
 * Loads the PDF fonts once per session. Await it before `createPdfDocument`;
 * `exportToPdf` does this itself. Never rejects — a failed load leaves the
 * built-in font in place and is retried on the next export.
 */
export const loadPdfFonts = (): Promise<void> => {
    if (!pdfFontLoad) {
        pdfFontLoad = Promise.all(PDF_FONTS.map(async (font) => {
            const response = await fetch(font.url);
            if (!response.ok) throw new Error(`font ${font.file}: ${response.status}`);
            return [font.file, toBase64(await response.arrayBuffer())] as const;
        }))
            .then((entries) => { pdfFontData = new Map(entries); })
            .catch(() => { pdfFontLoad = null; });
    }
    return pdfFontLoad;
};

const registerPdfFonts = (doc: jsPDF): void => {
    if (!pdfFontData) return;
    try {
        for (const font of PDF_FONTS) {
            doc.addFileToVFS(font.file, pdfFontData.get(font.file)!);
            for (const style of font.styles) doc.addFont(font.file, "helvetica", style);
        }
        doc.setFont("helvetica", "normal");
    } catch {
        // A font that fails to parse leaves the built-in Helvetica mapping.
    }
};

// ============================================================================
// Type Definitions
// ============================================================================

export interface PdfHeaderOptions {
    title: string;
    subtitle?: string;
    reference?: string;
    /** Small label above the title naming the document kind ("Client profile"). */
    eyebrow?: string;
    /** Tonal pill beside the title, like the record header on screen. */
    status?: { label: string; tone?: PdfTone };
    /** Kept for older callers — the masthead always carries the logo. */
    showLogo?: boolean;
    showDate?: boolean;
}

export interface PdfTableColumn {
    header: string;
    width?: number;
    halign?: "left" | "center" | "right";
}

export interface PdfTableOptions {
    columns: PdfTableColumn[];
    body: RowInput[];
    startY?: number;
    /** Faint zebra rows (default on). */
    showAlternateRows?: boolean;
    /** Kept for older callers; every table now shares one style. */
    variant?: "default" | "secondary";
    margin?: { left?: number; right?: number };
    headStylesOverride?: Partial<Styles>;
    bodyStylesOverride?: Partial<Styles>;
    alternateRowStylesOverride?: Partial<Styles>;
    tableLineColor?: PdfColor;
    tableLineWidth?: number;
}

export interface PdfExportOptions {
    fileName: string;
    orientation?: "portrait" | "landscape";
    format?: "a4" | "a3" | "letter";
    /** Page-one masthead. Omit when the builder draws its own cover page. */
    header?: PdfHeaderOptions;
    /** Running header on pages 2+. Defaults to the header's title + reference. */
    runningHeader?: { title: string; reference?: string };
    showFooter?: boolean;
    customContent?: (doc: jsPDF, yPos: number) => number;
    /**
     * Footer centre text — a fixed string ("Invoice INV-123") or per-page.
     * With `sectionState` it becomes "<centre> - <section>" per page.
     */
    footerCenter?: string | ((pageNum: number, totalPages: number) => string);
    sectionState?: SectionState;
    /** Small uppercase line above every footer ("CONFIDENTIAL — FIELD BRIEFING"). */
    classificationLabel?: string;
}

interface PdfDocMeta {
    runningHeader?: { title: string; reference?: string };
}

const docMeta = new WeakMap<jsPDF, PdfDocMeta>();

/** Sets the running header (pages 2+) for a document that draws its own cover. */
export const setPdfRunningHeader = (doc: jsPDF, runningHeader: { title: string; reference?: string }): void => {
    docMeta.set(doc, { ...docMeta.get(doc), runningHeader });
};

// ============================================================================
// Page chrome — masthead, running header, footer
// ============================================================================

/**
 * Small tonal pill (rounded, semibold). Pass a `tone` for status pills; the
 * legacy `fillColor` path paints white text on the given fill. Returns the
 * pill width so callers can lay several in a row.
 */
export const drawPdfPill = (
    doc: jsPDF,
    options: {
        x: number;
        /** Top y of the pill. */
        y: number;
        text: string;
        tone?: PdfTone;
        fillColor?: PdfColor;
        textColor?: PdfColor;
        fontSize?: number;
        padX?: number;
        height?: number;
    },
): number => {
    const tone = options.tone ? pdfTones[options.tone] : null;
    const fill = tone ? tone.bg : options.fillColor ?? pdfTones.neutral.bg;
    const ink = tone ? tone.fg : options.textColor ?? (options.fillColor ? palette.white : pdfTones.neutral.fg);
    const fontSize = options.fontSize ?? 7;
    const height = options.height ?? 4.8;
    const padX = options.padX ?? 2.4;

    setText(doc, ink, fontSize, "bold");
    const pillWidth = doc.getTextWidth(options.text) + padX * 2;
    doc.setFillColor(...fill);
    doc.roundedRect(options.x, options.y, pillWidth, height, height / 2, height / 2, "F");
    // Baseline that centres cap height (≈ 0.7 em) in the pill.
    const capHeight = fontSize * 0.3528 * 0.7;
    doc.text(options.text, options.x + padX, options.y + height / 2 + capHeight / 2);
    return pillWidth;
};

/** Draws the page-one masthead; returns the y where content starts. */
const drawMasthead = (doc: jsPDF, options: PdfHeaderOptions): number => {
    const pageWidth = pageWidthOf(doc);
    const contentWidth = pageWidth - PDF_MARGIN_X * 2;

    // Slate band — the app's header bar — with the logo and wordmark.
    doc.setFillColor(...palette.slate);
    doc.rect(0, 0, pageWidth, MASTHEAD_BAND_H, "F");
    const logoH = 8.4;
    const logoW = logoH * LOGO_RATIO;
    try {
        doc.addImage(crxLogo, "PNG", PDF_MARGIN_X, (MASTHEAD_BAND_H - logoH) / 2, logoW, logoH);
    } catch {
        // Logo failed to decode — the wordmark still brands the band.
    }
    setText(doc, palette.gold, 9, "bold");
    doc.text("CROWNOX", PDF_MARGIN_X + logoW + 3, 8.4, { charSpace: 0.45 });
    if (options.showDate !== false) {
        setText(doc, palette.inverseMuted, 7.5);
        doc.text(`Generated ${pdfDateTime(new Date())}`, pageWidth - PDF_MARGIN_X, 8.4, { align: "right" });
    }

    let y = MASTHEAD_BAND_H + 9;
    if (options.eyebrow) {
        setText(doc, palette.accentInk, 7.5, "bold");
        doc.text(options.eyebrow.toUpperCase(), PDF_MARGIN_X, y, { charSpace: LABEL_TRACKING * 1.5 });
        y += 7.5;
    } else {
        y += 4;
    }

    // Title with the status pill inline when both fit on one line, else the
    // pill leads the meta line.
    const titleSize = 17;
    const lineH = titleSize * 0.3528 * 1.2;
    const pillText = options.status?.label ? options.status.label.toUpperCase() : "";
    setText(doc, palette.strong, 7, "bold");
    const pillW = pillText ? doc.getTextWidth(pillText) + 4.8 : 0;
    setText(doc, palette.strong, titleSize, "bold");
    const pillInline = !!pillText && doc.getTextWidth(options.title) + 3 + pillW <= contentWidth;
    const titleLines: string[] = doc.splitTextToSize(options.title, contentWidth).slice(0, 2);
    doc.text(titleLines, PDF_MARGIN_X, y, { lineHeightFactor: 1.2 });
    const lastLineY = y + (titleLines.length - 1) * lineH;
    let metaX = PDF_MARGIN_X;
    if (pillText) {
        if (pillInline) {
            drawPdfPill(doc, { x: PDF_MARGIN_X + doc.getTextWidth(titleLines[0]) + 3, y: lastLineY - 4.6, text: pillText, tone: options.status?.tone ?? "neutral" });
        } else {
            metaX += drawPdfPill(doc, { x: PDF_MARGIN_X, y: lastLineY + 2.6, text: pillText, tone: options.status?.tone ?? "neutral" }) + 2.5;
        }
    }

    const meta = [options.subtitle, options.reference].filter(Boolean).join("   ·   ");
    if (meta || metaX > PDF_MARGIN_X) {
        y = lastLineY + 6.4;
        setText(doc, palette.muted, 8.5);
        doc.text(fitLine(doc, meta, contentWidth - (metaX - PDF_MARGIN_X)), metaX, y);
        y += 4.5;
    } else {
        y = lastLineY + 5.5;
    }
    hairline(doc, PDF_MARGIN_X, y, pageWidth - PDF_MARGIN_X);
    return y + 7;
};

/** Pages 2+: a quiet line naming the document, so a loose page stays identifiable. */
const drawRunningHeader = (doc: jsPDF, running: { title: string; reference?: string }): void => {
    const pageWidth = pageWidthOf(doc);
    const baseline = 9.2;
    setText(doc, palette.accentInk, 6.5, "bold");
    doc.text("CROWNOX", PDF_MARGIN_X, baseline, { charSpace: 0.35 });
    const brandW = trackedWidth(doc, "CROWNOX", 0.35);
    setText(doc, palette.heading, 7.5, "bold");
    const refW = running.reference ? doc.getTextWidth(running.reference) + 6 : 0;
    doc.text(fitLine(doc, running.title, pageWidth - PDF_MARGIN_X * 2 - brandW - 4 - refW), PDF_MARGIN_X + brandW + 4, baseline);
    if (running.reference) {
        setText(doc, palette.muted, 7.5);
        doc.text(running.reference, pageWidth - PDF_MARGIN_X, baseline, { align: "right" });
    }
    hairline(doc, PDF_MARGIN_X, 12, pageWidth - PDF_MARGIN_X);
};

/** One page's footer: optional classification line, rule, wordmark, centre text, page X of Y. */
const drawFooterOnCurrentPage = (
    doc: jsPDF,
    pageNumber: number,
    totalPages: number,
    centerText: string,
    classificationLabel?: string,
): void => {
    const pageWidth = pageWidthOf(doc);
    const pageHeight = pageHeightOf(doc);
    const ruleY = pageHeight - 13;
    const baseline = pageHeight - 8.2;

    if (classificationLabel) {
        setText(doc, palette.heading, 6.5, "bold");
        doc.text(classificationLabel.toUpperCase(), pageWidth / 2, ruleY - 2.2, { align: "center", charSpace: 0.3 });
    }
    hairline(doc, PDF_MARGIN_X, ruleY, pageWidth - PDF_MARGIN_X);

    setText(doc, palette.accentInk, 6.5, "bold");
    doc.text("CROWNOX", PDF_MARGIN_X, baseline, { charSpace: 0.35 });

    const pageLabel = `Page ${pageNumber} of ${totalPages}`;
    setText(doc, palette.muted, 7.5);
    doc.text(pageLabel, pageWidth - PDF_MARGIN_X, baseline, { align: "right" });
    if (centerText) {
        doc.text(fitLine(doc, centerText, pageWidth - 110), pageWidth / 2, baseline, { align: "center" });
    }
};

/**
 * Finishes every page: the running header on pages 2+ and the footer on all.
 * Centre text is fixed or per-page; with a `SectionState` the most recent
 * section name is appended ("Mission MIS-1 - Itinerary").
 */
export const applyBrandedPageFooter = (
    doc: jsPDF,
    options: {
        centerText?: string | ((pageNum: number, totalPages: number) => string);
        sectionState?: SectionState;
        /** Small uppercase line above every footer (briefings: "CONFIDENTIAL — FIELD BRIEFING"). */
        classificationLabel?: string;
    } = {},
): void => {
    const totalPages = doc.getNumberOfPages();
    const running = docMeta.get(doc)?.runningHeader;
    let lastSection = "";
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        const fixed = typeof options.centerText === "function"
            ? options.centerText(i, totalPages)
            : options.centerText ?? "";
        if (options.sectionState && options.sectionState.pageToSection.has(i)) {
            lastSection = options.sectionState.pageToSection.get(i)!;
        }
        const sectionPart = options.sectionState ? lastSection : "";
        let centerText = fixed;
        if (sectionPart) {
            centerText = centerText ? `${centerText}  -  ${sectionPart}` : sectionPart;
        }
        if (i > 1 && running) drawRunningHeader(doc, running);
        drawFooterOnCurrentPage(doc, i, totalPages, centerText, options.classificationLabel);
    }
};

// ============================================================================
// Public API
// ============================================================================

/**
 * Creates a new PDF document in the CRX style, with the page-one masthead
 * when `header` is given. Call `await loadPdfFonts()` first to get Plex.
 */
export const createPdfDocument = (options: PdfExportOptions): { doc: jsPDF; yPos: number } => {
    const doc = new jsPDF({
        orientation: options.orientation || "landscape",
        unit: "mm",
        format: options.format || "a4",
    });
    registerPdfFonts(doc);

    const runningHeader = options.runningHeader
        ?? (options.header ? { title: options.header.title, reference: options.header.reference } : undefined);
    docMeta.set(doc, { runningHeader });

    const yPos = options.header ? drawMasthead(doc, options.header) : PDF_CONTENT_TOP;
    return { doc, yPos };
};

/** "1,250.00", "(270.00)", "24%", "USD 2,730.00", "120.5 AED" — not phones, dates or refs. */
const NUMERIC_CELL = /^\(?-?(?:[A-Z]{3} ?)?[$€£¥]?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?(?: ?%| ?[A-Z]{3})?\)?$/;

const cellText = (cell: unknown): string => {
    if (cell === null || cell === undefined) return "";
    if (typeof cell === "object" && "content" in (cell as Record<string, unknown>)) return String((cell as { content: unknown }).content ?? "");
    return String(cell);
};

/** A column reads as numeric when every filled cell is a number, amount or percentage. */
const isNumericColumn = (body: RowInput[], index: number): boolean => {
    let filled = 0;
    for (const row of body) {
        if (!Array.isArray(row)) return false;
        const first = row[0] as unknown;
        // Group/total rows span columns — they don't describe the column.
        if (first && typeof first === "object" && Number((first as { colSpan?: number }).colSpan) > 1) continue;
        const text = cellText(row[index]).trim();
        if (!text || text === "-" || text === "—") continue;
        if (!NUMERIC_CELL.test(text)) return false;
        filled++;
    }
    return filled > 0;
};

/**
 * Renders a table in the shared style: light header row with small uppercase
 * labels, hairline rows, faint zebra, numbers right-aligned. Returns the y
 * below the table.
 */
export const renderPdfTable = (doc: jsPDF, options: PdfTableOptions): number => {
    const columnStyles: { [key: number]: Partial<Styles> } = {};

    // Distribute the given column widths proportionally over the available width.
    const pageWidth = pageWidthOf(doc);
    const leftMargin = options.margin?.left ?? PDF_MARGIN_X;
    const rightMargin = options.margin?.right ?? PDF_MARGIN_X;
    const availableWidth = pageWidth - leftMargin - rightMargin;
    const totalDefined = options.columns.reduce((sum, col) => sum + (col.width || 0), 0);
    const aligns = options.columns.map((col, index) =>
        col.halign || (isNumericColumn(options.body, index) ? "right" : "left"));

    options.columns.forEach((col, index) => {
        const cellWidth = col.width && totalDefined > 0
            ? (col.width / totalDefined) * availableWidth
            : undefined;
        columnStyles[index] = {
            ...(cellWidth ? { cellWidth } : {}),
            halign: aligns[index],
        };
    });

    const tableOptions: UserOptions = {
        startY: options.startY || PDF_CONTENT_TOP,
        head: [options.columns.map((col, index) => ({
            content: col.header.toUpperCase(),
            styles: { halign: aligns[index] },
        }))],
        body: options.body,
        theme: "plain",
        rowPageBreak: "avoid",
        margin: { top: PDF_CONTENT_TOP - 2, bottom: PDF_FOOTER_RESERVE, left: leftMargin, right: rightMargin },
        styles: {
            font: "helvetica",
            fontSize: 8.3,
            cellPadding: { top: 1.9, right: 2.4, bottom: 1.9, left: 2.4 },
            textColor: palette.body,
            lineColor: palette.border,
            lineWidth: { bottom: 0.15 },
            valign: "middle",
            overflow: "linebreak",
            ...(options.bodyStylesOverride ?? {}),
        },
        headStyles: {
            fillColor: palette.surfaceHeader,
            textColor: palette.muted,
            fontStyle: "bold",
            fontSize: 6.8,
            lineColor: palette.borderStrong,
            lineWidth: { bottom: 0.3 },
            cellPadding: { top: 2.3, right: 2.4, bottom: 2.1, left: 2.4 },
            ...(options.headStylesOverride ?? {}),
        },
        columnStyles,
    };

    if (options.showAlternateRows !== false) {
        tableOptions.alternateRowStyles = options.alternateRowStylesOverride ?? { fillColor: palette.zebra };
    } else if (options.alternateRowStylesOverride) {
        tableOptions.alternateRowStyles = options.alternateRowStylesOverride;
    }

    if (options.tableLineColor) {
        (tableOptions as any).tableLineColor = options.tableLineColor;
    }
    if (options.tableLineWidth != null) {
        (tableOptions as any).tableLineWidth = options.tableLineWidth;
    }

    autoTable(doc, tableOptions);

    return (doc as any).lastAutoTable?.finalY || options.startY || PDF_CONTENT_TOP;
};

// ============================================================================
// Headings
// ============================================================================

/**
 * Section heading: optional olive number, strong title, optional count pill,
 * a full-width hairline with a short gold accent under the number.
 * Returns the y where the section body starts.
 */
export const drawPdfSectionTitle = (
    doc: jsPDF,
    yPos: number,
    options: { title: string; number?: string; count?: number },
): number => {
    const pageWidth = pageWidthOf(doc);
    const baseline = yPos + 7.6;
    let cursor = PDF_MARGIN_X;

    if (options.number) {
        setText(doc, palette.accentInk, 9, "bold");
        doc.text(options.number, cursor, baseline);
        cursor += doc.getTextWidth(options.number) + 3;
    }
    setText(doc, palette.strong, 12, "bold");
    doc.text(options.title, cursor, baseline);
    cursor += doc.getTextWidth(options.title) + 2.5;
    if (options.count != null) {
        drawPdfPill(doc, { x: cursor, y: baseline - 3.9, text: String(options.count), tone: "neutral" });
    }

    const ruleY = yPos + 10;
    hairline(doc, PDF_MARGIN_X, ruleY, pageWidth - PDF_MARGIN_X, palette.borderStrong, 0.3);
    doc.setFillColor(...palette.gold);
    doc.rect(PDF_MARGIN_X, ruleY - 0.45, 12, 0.9, "F");
    return ruleY + 6;
};

/**
 * Quiet sub-heading inside a section: small uppercase muted label (+ count)
 * on a hairline. Returns the y below it.
 */
export const drawPdfSubsectionLabel = (doc: jsPDF, yPos: number, label: string, count?: number): number => {
    const pageWidth = pageWidthOf(doc);
    const text = label.toUpperCase();
    setText(doc, palette.muted, 7.5, "bold");
    doc.text(text, PDF_MARGIN_X, yPos + 5, { charSpace: LABEL_TRACKING });
    if (count != null) {
        drawPdfPill(doc, { x: PDF_MARGIN_X + trackedWidth(doc, text, LABEL_TRACKING) + 2, y: yPos + 1.6, text: String(count), tone: "neutral", fontSize: 6.5, height: 4.4, padX: 2 });
    }
    hairline(doc, PDF_MARGIN_X, yPos + 7, pageWidth - PDF_MARGIN_X);
    return yPos + 11;
};

/** Panel-style title for a table or list: strong title + count pill. Returns the baseline y. */
const drawPanelTitle = (doc: jsPDF, x: number, yPos: number, title: string, count?: number): void => {
    setText(doc, palette.strong, 9.5, "bold");
    doc.text(title, x, yPos);
    if (count != null) {
        drawPdfPill(doc, { x: x + doc.getTextWidth(title) + 2.2, y: yPos - 3.6, text: String(count), tone: "neutral", fontSize: 6.5, height: 4.4, padX: 2 });
    }
};

// ============================================================================
// Panels, stats, tables-in-a-panel
// ============================================================================

export interface PdfStat {
    label: string;
    value: string;
    /** Colours the value (status tones' solid ink). */
    tone?: PdfTone;
}

/**
 * A row of stats in a bordered card, split by hairlines — the record page's
 * stat strip. `compact` shrinks the values for key-fact rows (dates, refs).
 * Returns the y below the strip.
 */
export const drawPdfStatStrip = (
    doc: jsPDF,
    options: { y: number; stats: PdfStat[]; x?: number; width?: number; compact?: boolean },
): number => {
    const stats = options.stats.filter(Boolean);
    if (stats.length === 0) return options.y;
    const x = options.x ?? PDF_MARGIN_X;
    const width = options.width ?? pageWidthOf(doc) - PDF_MARGIN_X * 2;
    const height = options.compact ? 13 : 16;
    const y = ensurePdfSpace(doc, options.y, height + 2);
    const cellW = width / stats.length;

    doc.setDrawColor(...palette.border);
    doc.setFillColor(...palette.white);
    doc.setLineWidth(HAIRLINE);
    doc.roundedRect(x, y, width, height, PANEL_RADIUS, PANEL_RADIUS, "FD");
    for (let i = 1; i < stats.length; i++) {
        doc.line(x + i * cellW, y, x + i * cellW, y + height);
    }

    stats.forEach((stat, i) => {
        const cx = x + i * cellW + 3.6;
        const innerW = cellW - 7.2;
        setText(doc, palette.muted, 6.3, "bold");
        doc.text(fitLine(doc, stat.label.toUpperCase(), innerW), cx, y + 5, { charSpace: LABEL_TRACKING });
        const valueColor = stat.tone ? pdfTones[stat.tone].solid : palette.strong;
        setText(doc, valueColor, options.compact ? 9.3 : 13, "bold");
        doc.text(fitLine(doc, stat.value || "—", innerW), cx, y + (options.compact ? 10.3 : 12.6));
    });
    return y + height + 6;
};

interface SummaryLayout {
    cols: number;
    colW: number;
    rows: { item: { label: string; value: string; highlight?: boolean }; lines: string[] }[][];
    rowHeights: number[];
    height: number;
}

const SUMMARY_PAD_X = 4.5;
const SUMMARY_HEADER_H = 8.6;
const SUMMARY_VALUE_LINE = 4.0;

const layoutSummaryBox = (
    doc: jsPDF,
    width: number,
    items: { label: string; value: string; highlight?: boolean }[],
): SummaryLayout => {
    const cols = width >= 230 ? 3 : width >= 110 ? 2 : 1;
    const gap = 6;
    const colW = (width - SUMMARY_PAD_X * 2 - gap * (cols - 1)) / cols;
    const rows: SummaryLayout["rows"] = [];
    const rowHeights: number[] = [];
    for (let i = 0; i < items.length; i += cols) {
        const row = items.slice(i, i + cols).map((item) => {
            doc.setFont("helvetica", item.highlight ? "bold" : "normal");
            doc.setFontSize(8.8);
            const lines: string[] = doc.splitTextToSize(String(item.value ?? "").trim() || "—", colW);
            return { item, lines };
        });
        rows.push(row);
        const maxLines = Math.max(...row.map((cell) => cell.lines.length));
        rowHeights.push(7.4 + (maxLines - 1) * SUMMARY_VALUE_LINE + 2.4);
    }
    const height = SUMMARY_HEADER_H + 2.2 + rowHeights.reduce((sum, h) => sum + h, 0) + 0.8;
    return { cols, colW, rows, rowHeights, height };
};

/**
 * A titled panel of label / value fields — the web's Panel + FieldGrid. Values
 * wrap and the panel grows to fit; `height` is a minimum (e.g. beside a
 * photo). Moves to a new page rather than split. Returns the y below it.
 * `variant` is kept for older callers; every panel now shares one style.
 */
export const drawPdfSummaryBox = (
    doc: jsPDF,
    options: {
        x: number;
        y: number;
        width: number;
        height: number;
        title: string;
        items: { label: string; value: string; highlight?: boolean }[];
        variant?: "default" | "formal";
    },
): number => {
    const { x, width, title, items } = options;
    const layout = layoutSummaryBox(doc, width, items);
    const height = Math.max(layout.height, options.height || 0);
    let y = options.y;
    if (y + height > pageHeightOf(doc) - PDF_FOOTER_RESERVE && height < pageHeightOf(doc) - PDF_FOOTER_RESERVE - PDF_CONTENT_TOP) {
        doc.addPage();
        y = PDF_CONTENT_TOP;
    }

    doc.setDrawColor(...palette.border);
    doc.setFillColor(...palette.white);
    doc.setLineWidth(HAIRLINE);
    doc.roundedRect(x, y, width, height, PANEL_RADIUS, PANEL_RADIUS, "FD");
    setText(doc, palette.strong, 9, "bold");
    doc.text(fitLine(doc, title, width - SUMMARY_PAD_X * 2), x + SUMMARY_PAD_X, y + 5.7);
    hairline(doc, x, y + SUMMARY_HEADER_H, x + width);

    let rowY = y + SUMMARY_HEADER_H + 2.2;
    layout.rows.forEach((row, r) => {
        row.forEach(({ item, lines }, c) => {
            const cx = x + SUMMARY_PAD_X + c * (layout.colW + 6);
            setText(doc, palette.muted, 7.2);
            doc.text(fitLine(doc, item.label, layout.colW), cx, rowY + 3);
            setText(doc, palette.strong, 8.8, item.highlight ? "bold" : "normal");
            doc.text(lines, cx, rowY + 7.2, { lineHeightFactor: SUMMARY_VALUE_LINE / (8.8 * 0.3528) });
        });
        rowY += layout.rowHeights[r];
    });

    return y + height + 6;
};

/**
 * A table under a panel title. A title ending in "(N)" renders N as a count
 * pill, as does `count`. Returns the y below the table.
 * `boxVariant` is kept for older callers; every table now shares one style.
 */
export const renderPdfTableInBox = (
    doc: jsPDF,
    options: PdfTableOptions & {
        title: string;
        x?: number;
        boxWidth?: number;
        boxVariant?: "default" | "formal";
        count?: number;
    },
): number => {
    const x = options.x ?? PDF_MARGIN_X;
    const pageWidth = pageWidthOf(doc);
    const boxWidth = options.boxWidth ?? (pageWidth - PDF_MARGIN_X * 2);
    const rightMargin = pageWidth - x - boxWidth;
    const counted = options.count == null ? /^(.*?)\s*\((\d+)\)$/.exec(options.title) : null;
    const title = counted ? counted[1] : options.title;
    const count = options.count ?? (counted ? Number(counted[2]) : undefined);

    // Keep the title with the header row and at least one body row.
    const startY = ensurePdfSpace(doc, options.startY || PDF_CONTENT_TOP, 24);
    drawPanelTitle(doc, x, startY + 5.2, title, count);
    const finalY = renderPdfTable(doc, {
        ...options,
        startY: startY + 8,
        margin: { left: x, right: rightMargin },
    });
    return finalY + 8;
};

// ============================================================================
// Table row builders
// ============================================================================

/**
 * A bold totals row. `default` = subtotal (light fill), `highlight` = grand
 * total (slate with gold, like the primary button), `overtime` = warning tone.
 */
export const createTotalRow = (
    cells: (string | number)[],
    options?: {
        variant?: "default" | "highlight" | "overtime";
        colSpan?: number;
    },
): RowInput => {
    let fillColor: PdfColor;
    let textColor: PdfColor;

    switch (options?.variant) {
        case "highlight":
            fillColor = palette.slate;
            textColor = palette.gold;
            break;
        case "overtime":
            fillColor = pdfTones.warning.bg;
            textColor = pdfTones.warning.fg;
            break;
        default:
            fillColor = palette.surfaceHeader;
            textColor = palette.strong;
    }

    const colSpan = options?.colSpan || 1;
    const styles = { fontStyle: "bold" as const, fillColor, textColor };

    return cells.map((cell, index): CellInput => {
        if (index === 0 && colSpan > 1) {
            return { content: String(cell), colSpan, styles };
        }
        if (index > 0 && index < colSpan) {
            return "";
        }
        return { content: String(cell), styles };
    }).filter((_, index) => index === 0 || index >= colSpan);
};

/** A full-width group row (location, phase) inside a table. */
export const createSectionHeaderRow = (label: string, colSpan: number): RowInput => {
    return [{
        content: label,
        colSpan,
        styles: {
            fillColor: pdfTones.neutral.bg,
            textColor: palette.strong,
            fontStyle: "bold" as const,
            fontSize: 8.3,
        },
    }];
};

/** A cell flagged over (danger tone) or under (success tone); `normal` stays plain. */
export const createVarianceCell = (
    content: string,
    variant: "over" | "under" | "normal",
): { content: string; styles: Partial<Styles> } => {
    if (variant === "normal") {
        return { content, styles: {} };
    }
    const tone = variant === "over" ? pdfTones.danger : pdfTones.success;
    return {
        content,
        styles: { fillColor: tone.bg, textColor: tone.fg, fontStyle: "bold" },
    };
};

/**
 * Generates and saves a PDF: loads the fonts, draws the masthead, runs
 * `customContent`, then the page chrome.
 */
export const exportToPdf = async (options: PdfExportOptions): Promise<void> => {
    await loadPdfFonts();
    const { doc, yPos } = createPdfDocument(options);

    if (options.customContent) {
        options.customContent(doc, yPos);
    }

    if (options.showFooter !== false) {
        applyBrandedPageFooter(doc, {
            centerText: options.footerCenter,
            sectionState: options.sectionState,
            classificationLabel: options.classificationLabel,
        });
    }

    doc.save(`${options.fileName}.pdf`);
};

// ============================================================================
// Image Helpers
// ============================================================================

/**
 * Fetches an image from S3 as a base64 data URL through the backend.
 * Avoids CORS issues by proxying through the API.
 * @param s3Key - The S3 object key (e.g., "Contacts/REF-123/images/photo.png/photo.png")
 * @returns Base64 data URL string, or null if the image could not be loaded.
 */
export const fetchImageForExport = async (s3Key: string): Promise<string | null> => {
    try {
        const response = await getReq(`files/download-image?key=${encodeURIComponent(s3Key)}`);
        if (response.status === 200) {
            return response.body;
        }
    } catch {
        // Continue without image
    }
    return null;
};

/** Draws an image with rounded corners and a hairline frame. */
export const drawPdfRoundedImage = (doc: jsPDF, dataUrl: string, x: number, y: number, size: number): void => {
    try {
        doc.saveGraphicsState();
        doc.roundedRect(x, y, size, size, 2.2, 2.2, null);
        doc.clip();
        doc.discardPath();
        doc.addImage(dataUrl, "JPEG", x, y, size, size);
        doc.restoreGraphicsState();
    } catch {
        try {
            doc.addImage(dataUrl, "JPEG", x, y, size, size);
        } catch {
            return;
        }
    }
    doc.setDrawColor(...palette.border);
    doc.setLineWidth(HAIRLINE);
    doc.roundedRect(x, y, size, size, 2.2, 2.2, "S");
};

// ============================================================================
// Shared Profile Export Helpers
// ============================================================================

/**
 * Minimum panel height for `itemCount` fields. Panels size themselves to their
 * content now, so this is only a floor; kept for the callers that pass it.
 */
export const calcBoxHeight = (itemCount: number): number => Math.ceil(itemCount / 2) * 6 + 12;

/**
 * Section-tracking state for multi-section PDFs. `drawNumberedSectionBanner`
 * records the section name against its page; `applyBrandedPageFooter` reads it
 * back so every page's footer names its section ("Mission MIS-XXX - Itinerary").
 */
export interface SectionState {
    counter: number;
    pageToSection: Map<number, string>;
}

export const newSectionState = (firstPageLabel = "Cover"): SectionState => ({
    counter: 0,
    pageToSection: new Map([[1, firstPageLabel]]),
});

/**
 * Numbered section heading ("01  Contacts & Organizations"). Records the
 * section against the current page for the footer pass.
 */
export const drawNumberedSectionBanner = (
    doc: jsPDF,
    state: SectionState,
    yPos: number,
    title: string,
): number => {
    state.counter += 1;
    const next = drawPdfSectionTitle(doc, yPos, { title, number: String(state.counter).padStart(2, "0") });
    state.pageToSection.set(currentPage(doc), title);
    return next;
};

const NOTES_LINE = 4.4;
const NOTES_SIZE = 8.8;

/**
 * A labelled block of free text that wraps and flows across pages.
 * `variant` is kept for older callers; notes share one style.
 */
export const renderPdfNotesBox = (
    doc: jsPDF,
    options: { yPos: number; title: string; text: string; variant?: "default" | "formal" },
): number => {
    const pageHeight = pageHeightOf(doc);
    const textWidth = pageWidthOf(doc) - PDF_MARGIN_X * 2;
    const maxY = pageHeight - PDF_FOOTER_RESERVE;

    setText(doc, palette.body, NOTES_SIZE);
    const lines: string[] = doc.splitTextToSize(options.text, textWidth);

    // Keep the label with at least two lines of text.
    let yPos = ensurePdfSpace(doc, options.yPos, 11 + NOTES_LINE * 2);
    yPos = drawPdfSubsectionLabel(doc, yPos, options.title);
    let remaining = lines;
    while (remaining.length > 0) {
        const fit = Math.max(1, Math.floor((maxY - yPos) / NOTES_LINE));
        const chunk = remaining.slice(0, fit);
        remaining = remaining.slice(fit);
        setText(doc, palette.body, NOTES_SIZE);
        doc.text(chunk, PDF_MARGIN_X, yPos + 1, { lineHeightFactor: NOTES_LINE / (NOTES_SIZE * 0.3528) });
        yPos += chunk.length * NOTES_LINE;
        if (remaining.length > 0) {
            doc.addPage();
            yPos = PDF_CONTENT_TOP;
        }
    }
    return yPos + 6;
};

/**
 * Photo (rounded) beside the record's main fields panel.
 * Used in contact, client, and provider profile exports.
 */
export const renderPdfProfileHeader = (
    doc: jsPDF,
    options: {
        yPos: number;
        photoDataUrl: string | null;
        title: string;
        items: { label: string; value: string; highlight?: boolean }[];
    },
): number => {
    let yPos = options.yPos;
    const pageWidth = pageWidthOf(doc);
    const photoSize = 30;
    const hasPhoto = !!options.photoDataUrl;
    const boxX = hasPhoto ? PDF_MARGIN_X + photoSize + 5 : PDF_MARGIN_X;
    const boxWidth = hasPhoto ? pageWidth - PDF_MARGIN_X * 2 - photoSize - 5 : pageWidth - PDF_MARGIN_X * 2;

    // Page-break before the photo so it never strands above a moved panel.
    const needed = Math.max(options.items.length > 0 ? layoutSummaryBox(doc, boxWidth, options.items).height : 0, hasPhoto ? photoSize : 0);
    yPos = ensurePdfSpace(doc, yPos, needed);

    if (hasPhoto) drawPdfRoundedImage(doc, options.photoDataUrl!, PDF_MARGIN_X, yPos, photoSize);

    if (options.items.length > 0) {
        return drawPdfSummaryBox(doc, {
            x: boxX, y: yPos, width: boxWidth, height: hasPhoto ? photoSize : 0,
            title: options.title, items: options.items,
        });
    }
    return hasPhoto ? yPos + photoSize + 6 : yPos;
};

/**
 * Mission history: title + count, a stat strip of the status mix, then the
 * mission table.
 * @param counterpartyHeader - Column header for the counterparty ("Clients" or "Providers")
 * @param counterpartyField - Field name on mission search result ("clients" or "providers")
 */
export const renderPdfMissionHistory = (
    doc: jsPDF,
    options: {
        yPos: number;
        missions: any[];
        counterpartyHeader: string;
        counterpartyField: string;
    },
): number => {
    const { missions, counterpartyHeader, counterpartyField } = options;
    if (missions.length === 0) return options.yPos;

    let yPos = ensurePdfSpace(doc, options.yPos, 50);
    drawPanelTitle(doc, PDF_MARGIN_X, yPos + 5.2, "Mission history", missions.length);
    yPos += 8.5;

    const statusCounts: Record<string, number> = {};
    missions.forEach((m: any) => {
        if (m.status) statusCounts[m.status] = (statusCounts[m.status] || 0) + 1;
    });
    const stats: PdfStat[] = [
        { label: "Total", value: String(missions.length) },
        ...Object.entries(statusCounts).slice(0, 5).map(([status, count]) => ({
            label: status.charAt(0).toUpperCase() + status.slice(1),
            value: String(count),
        })),
    ];
    yPos = drawPdfStatStrip(doc, { y: yPos, stats, compact: true }) - 3;

    const tableEndY = renderPdfTable(doc, {
        columns: [
            { header: "Reference", width: 22 },
            { header: "Status", width: 16 },
            { header: "Start", width: 18 },
            { header: "End", width: 18 },
            { header: "Locations", width: 32 },
            { header: counterpartyHeader, width: 30 },
            { header: "Services", width: 30 },
        ],
        body: missions.map((m: any) => [
            m.referenceNumber || "",
            m.status || "",
            pdfDate(m.dateFrom),
            pdfDate(m.dateTo),
            m.locations || "",
            m[counterpartyField] || "",
            m.services || "",
        ]),
        startY: yPos,
    });

    return tableEndY + 8;
};

// ============================================================================
// Cover page
// ============================================================================

export interface PdfCoverOptions {
    /** Document kind above the title ("Mission operations briefing"). */
    eyebrow: string;
    /** The big line — usually the record reference. */
    title: string;
    subtitle?: string;
    /** Classification chip on the cover ("Confidential — field briefing"). */
    classification?: string;
    status?: { label: string; tone?: PdfTone };
    cancelled?: boolean;
    facts: { label: string; value: string }[];
    metrics?: PdfStat[];
    /** Bottom block: prepared for / issued / document id. */
    meta?: { label: string; value: string }[];
    disclaimer?: string;
}

/**
 * Full cover page for formal documents (briefings): a slate block with the
 * brand, document kind, reference and status, then the fact sheet, the key
 * metrics strip and the issue block. Draws on the current (first) page.
 */
export const drawPdfCoverPage = (doc: jsPDF, options: PdfCoverOptions): void => {
    const pageWidth = pageWidthOf(doc);
    const pageHeight = pageHeightOf(doc);
    const contentW = pageWidth - PDF_MARGIN_X * 2;
    const blockH = 112;

    doc.setFillColor(...palette.slate);
    doc.rect(0, 0, pageWidth, blockH, "F");
    const logoH = 13;
    try {
        doc.addImage(crxLogo, "PNG", PDF_MARGIN_X, 14, logoH * LOGO_RATIO, logoH);
    } catch {
        // Logo failed to decode — the wordmark still brands the cover.
    }
    setText(doc, palette.gold, 11, "bold");
    doc.text("CROWNOX", PDF_MARGIN_X + logoH * LOGO_RATIO + 4, 22.6, { charSpace: 0.7 });

    if (options.classification) {
        const label = options.classification.toUpperCase();
        setText(doc, palette.gold, 6.8, "bold");
        const chipW = trackedWidth(doc, label, 0.3) + 6;
        const chipX = pageWidth - PDF_MARGIN_X - chipW;
        doc.setDrawColor(...palette.gold);
        doc.setLineWidth(0.3);
        doc.roundedRect(chipX, 16.6, chipW, 6.4, 3.2, 3.2, "S");
        doc.text(label, chipX + 3, 20.9, { charSpace: 0.3 });
    }

    setText(doc, palette.gold, 9, "bold");
    doc.text(options.eyebrow.toUpperCase(), PDF_MARGIN_X, 64, { charSpace: 0.45 });
    setText(doc, palette.white, 30, "bold");
    const titleLines: string[] = doc.splitTextToSize(options.title, contentW).slice(0, 2);
    doc.text(titleLines, PDF_MARGIN_X, 78, { lineHeightFactor: 1.1 });
    let y = 78 + (titleLines.length - 1) * 11.6;
    if (options.subtitle) {
        setText(doc, palette.inverseMuted, 11);
        doc.text(fitLine(doc, options.subtitle, contentW), PDF_MARGIN_X, y + 9);
        y += 9;
    }
    let pillX = PDF_MARGIN_X;
    if (options.cancelled) {
        pillX += drawPdfPill(doc, { x: pillX, y: y + 6, text: "CANCELLED", fillColor: pdfTones.danger.solid, fontSize: 7.5, height: 5.6, padX: 3 }) + 2.5;
    }
    if (options.status?.label) {
        drawPdfPill(doc, { x: pillX, y: y + 6, text: options.status.label.toUpperCase(), tone: options.status.tone ?? "neutral", fontSize: 7.5, height: 5.6, padX: 3 });
    }

    // Fact sheet
    y = blockH + 14;
    const labelW = 44;
    options.facts.forEach((fact) => {
        setText(doc, palette.muted, 7, "bold");
        doc.text(fact.label.toUpperCase(), PDF_MARGIN_X, y, { charSpace: LABEL_TRACKING });
        setText(doc, palette.strong, 10);
        const lines: string[] = doc.splitTextToSize(fact.value || "—", contentW - labelW);
        doc.text(lines, PDF_MARGIN_X + labelW, y, { lineHeightFactor: 1.25 });
        y += Math.max(7.5, lines.length * 4.4 + 3.1);
    });

    if (options.metrics && options.metrics.length > 0) {
        y = drawPdfStatStrip(doc, { y: y + 3, stats: options.metrics });
    }

    // Issue block, pinned above the footer.
    const meta = options.meta ?? [];
    let metaY = pageHeight - 30 - meta.length * 5;
    if (meta.length > 0 || options.disclaimer) {
        hairline(doc, PDF_MARGIN_X, metaY - 6, pageWidth - PDF_MARGIN_X);
    }
    meta.forEach((row) => {
        setText(doc, palette.muted, 7, "bold");
        doc.text(row.label.toUpperCase(), PDF_MARGIN_X, metaY, { charSpace: LABEL_TRACKING });
        setText(doc, palette.body, 8.8);
        doc.text(fitLine(doc, row.value, contentW - labelW), PDF_MARGIN_X + labelW, metaY);
        metaY += 5;
    });
    if (options.disclaimer) {
        setText(doc, palette.muted, 7.2, "italic");
        doc.text(doc.splitTextToSize(options.disclaimer, contentW), PDF_MARGIN_X, metaY + 2);
    }
};
